import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button-simple';
import { Input } from '../ui/input-simple';
import { Label } from '../ui/label';
import { Trash2, Edit2, Plus, Star } from 'lucide-react';
import { adminOffers as initialAdminOffers } from '../../data/mockData';
import { servicesAPI } from '../../services/api';
import { Toast, useToast } from '../shared/Toast';

const STORAGE_KEY = 'bciis_admin_offers_v1';
const FEATURED_KEY = 'bciis_featured_services';

export default function AdminServiceOffers({ hideList = false }) {
  const [offers, setOffers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', contact: '', active: true });
  const [featuredIds, setFeaturedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FEATURED_KEY)) || []; } catch { return []; }
  });
  const { message, showSuccess, showError, dismiss } = useToast();

  useEffect(() => {
    const load = async () => {
      // Prefer server API when available, otherwise localStorage/mock fallback
      try {
        if (servicesAPI && servicesAPI.getAll) {
          const res = await servicesAPI.getAll();
          const data = Array.isArray(res) ? res : (res?.data || res?.rows || []);
          setOffers(data.map(item => ({ ...item, id: item.id || item.service_id })));
          return;
        }
      } catch (e) {
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          setOffers(JSON.parse(stored));
          return;
        } catch (e) {
        }
      }
      setOffers(initialAdminOffers || []);
    };
    load();
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(offers));
  }, [offers]);

  useEffect(() => {
    localStorage.setItem(FEATURED_KEY, JSON.stringify(featuredIds));
  }, [featuredIds]);

  const toggleFeatured = (id) => {
    setFeaturedIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
  };

  const resetForm = () => setForm({ title: '', description: '', contact: '', active: true });

  const handleAdd = () => {
    if (!form.title.trim()) return;
    const newOffer = { id: `offer-${Date.now()}`, ...form };
    // Try server create then fallback to local
    (async () => {
      try {
        if (servicesAPI && servicesAPI.create) {
          await servicesAPI.create(newOffer);
          // Reload full list from server so IDs and fields are correct
          const res = await servicesAPI.getAll();
          const data = Array.isArray(res) ? res : (res?.data || res?.rows || []);
          setOffers(data.map(item => ({ ...item, id: item.id || item.service_id })));
          resetForm();
          showSuccess('Service offer added successfully!');
          return;
        }
      } catch (e) {
        showError('Failed to add service offer. Please try again.');
      }

      setOffers(prev => [newOffer, ...prev]);
      resetForm();
      showSuccess('Service offer added locally!');
    })();
  };

  const handleEdit = (offer) => {
    setEditing(offer.id);
    setForm({ title: offer.title, description: offer.description, contact: offer.contact, active: !!offer.active });
  };

  const handleSaveEdit = () => {
    (async () => {
      try {
        if (servicesAPI && servicesAPI.update) {
          await servicesAPI.update(editing, form);
          // Reload full list from server
          const res = await servicesAPI.getAll();
          const data = Array.isArray(res) ? res : (res?.data || res?.rows || []);
          setOffers(data.map(item => ({ ...item, id: item.id || item.service_id })));
          setEditing(null);
          resetForm();
          showSuccess('Service offer updated successfully!');
          return;
        }
      } catch (e) {
        showError('Failed to update service offer. Please try again.');
      }

      setOffers(prev => prev.map(o => (o.id === editing ? { ...o, ...form } : o)));
      setEditing(null);
      resetForm();
      showSuccess('Service offer updated locally!');
    })();
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this service offer?')) return;
    (async () => {
      try {
        if (servicesAPI && servicesAPI.delete) {
          await servicesAPI.delete(id);
          setOffers(prev => prev.filter(o => o.id !== id));
          showSuccess('Service offer deleted successfully!');
          return;
        }
      } catch (e) {
        showError('Failed to delete service offer. Please try again.');
      }
      setOffers(prev => prev.filter(o => o.id !== id));
      showSuccess('Service offer deleted locally!');
    })();
  };

  return (
    <div>
      <Toast message={message} onDismiss={dismiss} className="mb-4" />

      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Form Header */}
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-amber-900">Admin Service Offers</h2>
            <span className="text-sm text-amber-700 font-medium">Create and manage services offered to users</span>
          </div>
        </div>

        {/* Form Body */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1.5 block">Title</Label>
                <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))} placeholder="Service title" className="rounded-xl border-gray-300 focus:border-amber-500 focus:ring-amber-500" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1.5 block">Contact</Label>
                <Input value={form.contact} onChange={(e) => setForm(prev => ({ ...prev, contact: e.target.value }))} placeholder="Email or phone" className="rounded-xl border-gray-300 focus:border-amber-500 focus:ring-amber-500" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1.5 block">Description</Label>
              <textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-3 rounded-xl border border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none resize-none transition-colors"
                placeholder="Short description"
                rows={4}
              />
            </div>
          </div>

          {/* Action Button Row */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            {editing ? (
              <>
                <Button variant="outline" onClick={() => { setEditing(null); resetForm(); }} className="rounded-xl px-5">Cancel</Button>
                <Button onClick={handleSaveEdit} className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-5">Save Changes</Button>
              </>
            ) : (
              <Button onClick={handleAdd} className="bg-amber-700 hover:bg-amber-800 text-white rounded-xl px-6 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Offer
              </Button>
            )}
          </div>
        </div>
      </div>

      {!hideList && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {offers.length === 0 && (
            <div className="text-sm text-gray-500">No service offers yet.</div>
          )}

          {offers.map((offer) => (
            <div key={offer.id} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-md hover:shadow-lg hover:border-amber-300 transition-all duration-300">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-900 text-base">{offer.title}</h4>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{offer.description}</p>
                  <p className="text-xs text-gray-500 mt-2">Contact: {offer.contact || '—'}</p>
                </div>
                <div className="flex flex-col items-end gap-2 ml-3 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => toggleFeatured(offer.id || offer.service_id)}
                      className="p-1.5 rounded-lg hover:bg-amber-100 transition-colors"
                      title={featuredIds.includes(offer.id || offer.service_id) ? 'Remove from featured' : 'Mark as featured'}
                    >
                      <Star
                        className={`w-4 h-4 transition-colors ${
                          featuredIds.includes(offer.id || offer.service_id)
                            ? 'text-amber-500 fill-amber-500'
                            : 'text-gray-300 hover:text-amber-400'
                        }`}
                      />
                    </button>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${offer.active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>{offer.active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(offer)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors"><Edit2 className="w-4 h-4 text-blue-600" /></button>
                    <button onClick={() => handleDelete(offer.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
